// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { InputBox } from '@imprivata-cloud/components';
import { Form } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContinueButton from '../../../components/continueButton/ContinueButton';
import './OrganizationRoute.less';
import { StorageKeys, SystemType } from '@api/constants';
import { orgInfoGet } from '@api/setupServices';
import {
  FileAccessModifier,
  type OrgInfoGetResponse,
  type PrepareFileUploadResponse,
} from '@api/types';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { setToLocalStorage } from '@utils/utils';
import type { RcFile } from 'antd/es/upload';
import * as api from '../../../api/setupServices';
import DefaultLogo from '../../../assets/default_logo.svg';
import LogoUpload from '../../../components/logoUpload/LogoUpload';
import { STEPS, TRANSLATION_BUTTON } from '../../constants';
import { useNavigateToNextStep } from '../../hooks/useNavigateToNextStep';
import { getBase64 } from '../../hooks/useOrganizationInfoSave';
import type { ValidationRule } from '../../../components/validationRules/ValidationRules';

enum FieldNames {
  ORG_NAME = 'orgName',
  EMAIL = 'email',
  LOGO = 'logo',
}

interface FormValues {
  [FieldNames.ORG_NAME]: string;
  [FieldNames.EMAIL]: string;
  [FieldNames.LOGO]: RcFile;
}

const TRANSLATION_ROOT = 'setup.org-info.content';
const TRANSLATION_ERRORS = 'setup.external-idp-info.content.validation';

const OrganizationRoute = () => {
  const [isConfirmation, setIsConfirmation] = useState<boolean>(false);
  const [form] = Form.useForm<FormValues>();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const navigateToNextStep = useNavigateToNextStep();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const { t } = useTranslation();
  const [orgInfoGetResponse, setOrgInfoGetResponse] = useState<OrgInfoGetResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const emailRules: ValidationRule[] = [
    {
      id: 'email',
      label: t(`${TRANSLATION_ERRORS}.enter-valid-email`),
      validate: value => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

        if (!emailRegex.test(value)) {
          return false;
        }

        const [localPart, domain] = value.split('@');

        // Local part cannot start/end with '.' or contain '..'
        if (localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) {
          return false;
        }

        // Domain validation
        if (
          domain.startsWith('.') ||
          domain.endsWith('.') ||
          domain.startsWith('-') ||
          domain.endsWith('-') ||
          domain.includes('..') ||
          domain.includes('--')
        ) {
          return false;
        }

        // Only allowed characters
        if (!/^[\p{L}\p{N}._+-]+@[\p{L}\p{N}.-]+$/u.test(value)) {
          return false;
        }

        // Block restricted Unicode scripts
        const restrictedUnicodeRegex =
          /[\p{Script=Han}\p{Script=Hangul}\p{Script=Arabic}\p{Script=Hiragana}\p{Script=Katakana}]/u;

        if (restrictedUnicodeRegex.test(value)) {
          return false;
        }

        return true;
      },
    },
  ];

  const isEmailValid = (value: string) => emailRules.every(rule => rule.validate(value));

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const orgInfo = await orgInfoGet();
      if (!orgInfo?.orgName) {
        setIsLoading(false);
        return;
      }
      setOrgInfoGetResponse(orgInfo);
      form.setFieldValue(FieldNames.ORG_NAME, orgInfo.orgName);
      form.setFieldValue(FieldNames.EMAIL, orgInfo.businessEmail);
      setEmail(orgInfo.businessEmail);
    } catch (e) {
      emitError(new AppError(e as ApiError));
    } finally {
      setIsLoading(false);
    }
  }, [form.setFieldValue, emitError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const onConfirm = useCallback(async () => {
    try {
      setIsLoading(true);
      setToLocalStorage(StorageKeys.ORG_EMAIL, form.getFieldValue(FieldNames.EMAIL));

      let uploadLinks: PrepareFileUploadResponse | undefined;

      const logoFile: File = form.getFieldValue(FieldNames.LOGO);
      if (logoFile) {
        uploadLinks = await api.getUploadUrl(
          logoFile.name,
          FileAccessModifier.PUBLIC,
          SystemType.SETUP,
        );
        await api.uploadFile(logoFile, uploadLinks?.uploadUrl || '');
      }

      await api.saveOrgInfo({
        logoUrl: uploadLinks?.readUrl as string,
        orgName: form.getFieldValue(FieldNames.ORG_NAME),
        businessEmail: form.getFieldValue(FieldNames.EMAIL),
      });

      navigateToNextStep(STEPS.IMPRIVATA_IDP);
    } catch (error) {
      if (error instanceof AppError) {
        emitError(error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [form, navigateToNextStep, emitError]);

  function showConfirmation() {
    setIsConfirmation(true);
  }

  function showForm() {
    setIsConfirmation(false);
  }

  const closeBannerIfExists = () => {
    clearErrorNotifications();
  };

  // Rendering this way is important
  // If you remove and then show the form on back to form, you lose is dirty and Continue button gets disabled
  return (
    <>
      {isConfirmation && (
        <ConfirmScreen
          data={form.getFieldsValue()}
          oldImageUrl={orgInfoGetResponse?.logoUrl}
          goBack={showForm}
          onConfirm={onConfirm}
          isLoading={isLoading}
        />
      )}
      <div style={{ display: isConfirmation ? 'none' : 'block' }}>
        <Form form={form} layout="vertical" onFinish={showConfirmation}>
          <div className={'subtitle'} />

          {/* Organization Name */}
          <Form.Item name={FieldNames.ORG_NAME} rules={[{ required: true }]}>
            <InputBox label={t(`${TRANSLATION_ROOT}.name-label`)} required autoFocus />
          </Form.Item>

          <LogoUpload
            fieldName={FieldNames.LOGO}
            fieldValue={form.getFieldValue(FieldNames.LOGO)}
            oldImageUrl={orgInfoGetResponse?.logoUrl}
            isLoading={isLoading}
          />

          {/* Organization Email */}
          <Form.Item
            name={FieldNames.EMAIL}
            validateStatus={emailError ? 'error' : undefined}
            help={emailError ? t(`${TRANSLATION_ERRORS}.enter-valid-email`) : undefined}
          >
            <InputBox
              label={t(`${TRANSLATION_ROOT}.email-label`)}
              required
              value={email}
              onChange={e => {
                const value = e.target.value;

                setEmail(value);
                form.setFieldValue(FieldNames.EMAIL, value);

                if (emailError) {
                  setEmailError(!isEmailValid(value));
                }
              }}
              onBlur={() => {
                setEmailError(!isEmailValid(email));
              }}
            />
          </Form.Item>

          {/* Continue */}
          <ContinueButton
            label={t(TRANSLATION_BUTTON)}
            htmlType="submit"
            style={{ marginTop: '40px' }}
            overrideErrorCheck={() => emailError}
            onClick={closeBannerIfExists}
            validateDirty={
              !orgInfoGetResponse?.orgName ? [FieldNames.ORG_NAME, FieldNames.EMAIL] : false
            }
          />
        </Form>
      </div>
    </>
  );
};

const ConfirmScreen = ({
  data,
  oldImageUrl,
  goBack,
  onConfirm,
  isLoading,
}: {
  data: FormValues;
  oldImageUrl?: string;
  goBack: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();
  const TRANSLATION_ROOT = 'setup.org-info-confirm';
  const [imageUrl, setImageUrl] = useState<string | undefined>();

  useEffect(() => {
    console.log('[OrganizationVerify] useEffect, data[LOGO]:', data[FieldNames.LOGO]);
    if (data[FieldNames.LOGO]) {
      // no error handling since if this fails, it fails in the LogoUpload
      getBase64(data[FieldNames.LOGO]).then(url => setImageUrl(url));
    }
  }, [data[FieldNames.LOGO]]);

  return (
    <div>
      <p>{t(`${TRANSLATION_ROOT}.content.label`)}</p>
      <p>{t(`${TRANSLATION_ROOT}.content.name-logo-label`)}</p>
      <h2>{data?.[FieldNames.ORG_NAME]}</h2>
      <img src={imageUrl || oldImageUrl || DefaultLogo} alt="org logo" />
      <Form style={{ marginTop: '10rem' }}>
        <ContinueButton
          className="focus-outline"
          disabled={isLoading}
          autoFocus
          secondaryButton={{ label: t('common.back-button'), onClick: goBack }}
          onClick={onConfirm}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </Form>
    </div>
  );
};

export default OrganizationRoute;
